import React, { useState } from 'react'
import type { Thread } from '../lib/types'

const EMOTION_SUGGESTIONS = [
  'split-root vigilance',
  'transcript armor',
  'office-hour debt',
  'family translator mask',
  'proof burden',
  'code-switch fatigue',
  'belonging debt',
  'first-at-the-table weight',
  'hypervigilance hangover',
  'legacy pressure',
  'erasure residue',
  'model-minority mask',
  'root-split ache',
  'translated self',
  'quiet exit planning',
]

interface ShuttleFormProps {
  onThrow: (note: Omit<Thread, 'id' | 'loomId' | 'createdAt' | 'cycle'>) => void
  disabled?: boolean
  defaultResonanceWith?: { emotionName: string; snippet: string } | null
}

export const ShuttleForm: React.FC<ShuttleFormProps> = ({ onThrow, disabled, defaultResonanceWith }) => {
  const [emotionName, setEmotionName] = useState(defaultResonanceWith?.emotionName || '')
  const [note, setNote] = useState(defaultResonanceWith ? `Re-weaving: ${defaultResonanceWith.snippet}` : '')
  const [frag, setFrag] = useState(5.5)
  const [recovery, setRecovery] = useState(6)
  const [readiness, setReadiness] = useState(4.5)
  const [clarity, setClarity] = useState(4)

  const [showSuggestions, setShowSuggestions] = useState(false)

  const canSubmit = emotionName.trim().length > 1 && note.trim().length > 3 && !disabled

  function submit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSubmit) return

    onThrow({
      emotionName: emotionName.trim().slice(0, 64),
      note: note.trim().slice(0, 158),
      frag: Math.round(frag * 10) / 10,
      recovery: Math.round(recovery * 10) / 10,
      readiness: Math.round(readiness * 10) / 10,
      clarity: Math.round(clarity * 10) / 10,
    })

    // Gentle reset — keep the last emotion as a thread of continuity, clear the note
    setNote('')
    // Small chance to surface a different suggestion next time
    if (Math.random() > 0.6) setEmotionName('')
  }

  const handleSuggestion = (s: string) => {
    setEmotionName(s)
    setShowSuggestions(false)
  }

  return (
    <form onSubmit={submit} className="card p-5 space-y-5">
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="text-sm font-medium text-[#3f2a1d]">Name the load</label>
          <button
            type="button"
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="text-xs text-[#9c6644] hover:underline"
          >
            {showSuggestions ? 'hide suggestions' : 'suggestions for first-gen loads'}
          </button>
        </div>

        <input
          type="text"
          value={emotionName}
          onChange={(e) => setEmotionName(e.target.value)}
          placeholder="e.g. split-root vigilance"
          className="w-full border border-[#d4c8b5] bg-white px-3 py-2 rounded focus:outline-none text-[15px]"
          maxLength={64}
          disabled={disabled}
        />

        {showSuggestions && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {EMOTION_SUGGESTIONS.map((sug) => (
              <button
                key={sug}
                type="button"
                onClick={() => handleSuggestion(sug)}
                className="text-xs px-2.5 py-px rounded-full border border-[#d4c8b5] hover:bg-[#f8f4ed] active:bg-white"
              >
                {sug}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-sm font-medium text-[#3f2a1d] block mb-1.5">Status threat note (what it actually feels like)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="The moment I have to explain where I come from again, something in my chest tightens and I lose the thread of what I was saying..."
          rows={3}
          className="w-full border border-[#d4c8b5] bg-white px-3 py-2 rounded resize-y min-h-[78px] focus:outline-none text-[15px] leading-snug"
          maxLength={158}
          disabled={disabled}
        />
        <div className="text-right text-[10px] text-[#8a7560] tabular-nums mt-px">{note.length}/158</div>
      </div>

      <div className="space-y-4 pt-1">
        <Slider label="Attention fragmentation" value={frag} setValue={setFrag} minLabel="whole" maxLabel="shattered" />
        <Slider label="Recovery need" value={recovery} setValue={setRecovery} minLabel="enough" maxLabel="urgent" warm />
        <Slider label="Readiness state" value={readiness} setValue={setReadiness} minLabel="depleted" maxLabel="on" />
        <Slider label="Post-reset clarity" value={clarity} setValue={setClarity} minLabel="fog" maxLabel="clear" earth />
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        className="shuttle-btn w-full py-3.5 text-base font-medium tracking-[0.3px] bg-[#2f3459] hover:bg-[#232744] active:bg-black text-white rounded disabled:bg-[#5c4636] disabled:text-[#d4c8b5] transition flex items-center justify-center gap-2"
      >
        <span>THROW THE SHUTTLE</span>
        <span aria-hidden>→</span>
      </button>

      <p className="text-[11px] text-center text-[#8a7560]">
        Your note is anonymous even to your roommates. Only the emotion name and the four numbers become part of the visible cloth.
      </p>
    </form>
  )
}

function Slider({
  label,
  value,
  setValue,
  minLabel,
  maxLabel,
  warm,
  earth,
}: {
  label: string
  value: number
  setValue: (n: number) => void
  minLabel: string
  maxLabel: string
  warm?: boolean
  earth?: boolean
}) {
  const cls = warm ? 'warm' : earth ? 'earth' : 'cool'
  return (
    <div>
      <div className="slider-group">
        <div className="slider-label">{label}</div>
        <div className="text-right tabular-nums text-sm font-medium text-[#3f2a1d]">{value.toFixed(1)}</div>
      </div>
      <div className="flex items-center gap-3 mt-1">
        <span className="text-[10px] text-[#8a7560] w-11 text-right shrink-0">{minLabel}</span>
        <input
          type="range"
          min={0}
          max={10}
          step={0.1}
          value={value}
          onChange={(e) => setValue(parseFloat(e.target.value))}
          className={`thread-slider flex-1 ${cls}`}
        />
        <span className="text-[10px] text-[#8a7560] w-11 shrink-0">{maxLabel}</span>
      </div>
    </div>
  )
}
